import { t } from '@lingui/core/macro';

import type { BadgeColors } from '@/components/base/badges/badge-types';

import {
  RespondentPayoutStatus,
  ScreenerResponseOutcome,
  ScreenerResponseStatus,
  type PayoutResultDto,
} from '@/api/core-api';
import { BadgeWithDot } from '@/components/base/badges/badges';

const STATUS_LABELS: Record<ScreenerResponseStatus, string> = {
  [ScreenerResponseStatus.PENDING]: t`Applied`,
  // An application still with the partner reads as an unfinished one — never
  // a distinct partner-facing state.
  [ScreenerResponseStatus.EXTERNAL_SCREENER_IN_PROGRESS]: t`Draft`,
  [ScreenerResponseStatus.INVITED]: t`Invited`,
  [ScreenerResponseStatus.SCHEDULED]: t`Scheduled`,
  [ScreenerResponseStatus.ATTENDED]: t`Attended`,
  [ScreenerResponseStatus.PAID]: t`Paid`,
  [ScreenerResponseStatus.DRAFT]: t`Draft`,
  [ScreenerResponseStatus.CANCELLED]: t`Cancelled`,
  [ScreenerResponseStatus.NOSHOW]: t`No Show`,
  [ScreenerResponseStatus.INVALIDRESPONSE]: t`Invalid Response`,
  [ScreenerResponseStatus.REJECTEDQUALITY]: t`Disqualified`,
};

interface ProjectStatusBadgeProps {
  status: ScreenerResponseStatus | undefined;
  /** The revealed screener verdict, when the server included one. */
  outcome?: ScreenerResponseOutcome;
  payout?: PayoutResultDto;
  isProjectClosed: boolean;
  isClosedToNewApplications: boolean;
  /**
   * The external task is done but not yet confirmed. The response is still
   * INVITED (or SCHEDULED), which would otherwise read "Invited".
   */
  isTaskComplete?: boolean;
}

export function ProjectStatusBadge(props: ProjectStatusBadgeProps) {
  const badge = resolveBadge(props);

  if (!badge) return null;

  return (
    <BadgeWithDot
      color={badge.color}
      className="border-none bg-transparent p-0 font-semibold ring-transparent"
      size="md"
    >
      {badge.label}
    </BadgeWithDot>
  );
}

function resolveBadge({
  status,
  outcome,
  payout,
  isProjectClosed,
  isClosedToNewApplications,
  isTaskComplete = false,
}: ProjectStatusBadgeProps): { color: BadgeColors; label: string } | null {
  if (status === ScreenerResponseStatus.PAID) {
    const isSettling =
      payout?.payoutStatus === RespondentPayoutStatus.PENDING ||
      payout?.payoutStatus === RespondentPayoutStatus.PROCESSING;

    return {
      color: 'success',
      label: isSettling ? t`Payment Processing` : t`Paid`,
    };
  }

  // The verdict outranks Closed, as it does on the apply page.
  if (
    status === ScreenerResponseStatus.PENDING &&
    outcome === ScreenerResponseOutcome.NOT_QUALIFIED
  ) {
    return { color: 'gray', label: t`Not a fit` };
  }

  // Outranks Closed: a study can finish while its last completions still
  // await confirmation, and "Closed" would hide that they did the work.
  if (
    isTaskComplete &&
    (status === ScreenerResponseStatus.INVITED ||
      status === ScreenerResponseStatus.SCHEDULED)
  ) {
    return { color: 'success', label: t`Complete` };
  }

  if (isProjectClosed || (isClosedToNewApplications && !status)) {
    return { color: 'gray', label: t`Closed` };
  }

  if (!status) return null;

  return { color: getStatusColor(status), label: STATUS_LABELS[status] };
}

function getStatusColor(status: ScreenerResponseStatus): BadgeColors {
  switch (status) {
    case ScreenerResponseStatus.INVITED:
      return 'warning';
    case ScreenerResponseStatus.INVALIDRESPONSE:
      return 'error';
    case ScreenerResponseStatus.SCHEDULED:
      return 'purple';
    case ScreenerResponseStatus.ATTENDED:
      return 'success';
    case ScreenerResponseStatus.NOSHOW:
      return 'error';
    case ScreenerResponseStatus.PAID:
      return 'success';
    case ScreenerResponseStatus.CANCELLED:
      return 'error';
    case ScreenerResponseStatus.DRAFT:
    case ScreenerResponseStatus.EXTERNAL_SCREENER_IN_PROGRESS:
      return 'gray';
    case ScreenerResponseStatus.PENDING:
      return 'blue';
    case ScreenerResponseStatus.REJECTEDQUALITY:
      return 'error';
  }
}
